import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import { compareDesc } from 'date-fns'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { BASE_URL } from '../../../../ini.json'
import { SenderAddress } from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import { stringSorter } from '../../utils/generalTableHelpers'
import { statisticPagination } from '../../utils/layoutConst'
import { StatisticCompany } from '../../utils/statisticHelpers'
import { Day, Min } from '../DateFormat/DateFormat'

interface PostServiceStatisticsTableItem {
  accountId: string
  createdAt: Date
  dispatchDate?: Maybe<Date>
  name?: string
  description?: string
  id: string
  recipients: { totalCount: number }
  senderAddress: SenderAddress
}
export interface PostServiceStatisticsTableProps {
  companies?: StatisticCompany
}

export const PostServiceStatisticsTable: FunctionComponent<PostServiceStatisticsTableProps> =
  props => {
    const token = useAppSelector(state => state.myProfile.token)
    const [loading, setLoading] = useState<boolean | undefined>()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [tableData, setTableData] = useState<any>()

    const fetchData = useCallback(async () => {
      setLoading(true)

      fetch(`${BASE_URL}/api/postService/admin/statistics/${token}`)
        .then(response => response.json())
        .then(data => {
          setTableData(data)
          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
          setTableData([])
        })
    }, [token])

    useEffect(() => {
      if (tableData !== undefined) return
      fetchData()
    }, [tableData, fetchData])

    const columns = useMemo<ColumnsType<PostServiceStatisticsTableItem>>(
      () => [
        {
          title: '#',
          key: 'index',
          className: 'w-8',
          render: (_, __, index) => index + 1,
        },
        {
          title: 'id',
          dataIndex: 'id',
          ellipsis: { showTitle: true },
          className: 'w-14 cursor-help',
        },
        {
          title: 'Vytvořeno',
          dataIndex: 'createdAt',
          className: 'w-24',
          sorter: (a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt) : new Date()
            const dateB = b.createdAt ? new Date(b.createdAt) : new Date()
            return compareDesc(dateA, dateB)
          },
          render: createdAt => <Day>{createdAt}</Day>,
        },
        {
          title: 'Odesláno',
          dataIndex: 'dispatchDate',
          className: 'w-32',
          defaultSortOrder: 'ascend',
          sorter: (a, b) => {
            const dateA = a.dispatchDate ? new Date(a.dispatchDate) : new Date()
            const dateB = b.dispatchDate ? new Date(b.dispatchDate) : new Date()
            return compareDesc(dateA, dateB)
          },
          render: dispatchDate => <Min>{dispatchDate}</Min>,
        },
        {
          title: 'Klient',
          dataIndex: 'accountId',
          ellipsis: { showTitle: true },
          sorter: stringSorter('accountId'),
          showSorterTooltip: { title: 'Řadí podle ID, ne názvu' },
          render: id => (
            <a
              className="!text-primary"
              href={`https://data.regesta.cz/PortalAdmin/DetailFirmy/${id}`}
              target="_blank"
              rel="noreferrer"
            >
              {props.companies?.[id] || id}
            </a>
          ),
        },
        {
          title: 'Název dopisu',
          dataIndex: 'name',
          ellipsis: { showTitle: true },
          sorter: stringSorter('name'),
        },
        {
          title: 'Popis dopisu',
          dataIndex: 'description',
          ellipsis: { showTitle: true },
          sorter: stringSorter('description'),
        },
        {
          title: 'Počet adresátů',
          dataIndex: 'recipientsCount',
          className: 'w-32',
          sorter: {
            compare: (a, b) =>
              a.recipients.totalCount - b.recipients.totalCount,
          },
          render: (_, record) => record.recipients.totalCount || 0,
        },

        {
          title: 'Odesílatel',
          dataIndex: 'senderAddress',
          sorter: {
            compare: (a, b) =>
              a?.senderAddress && b?.senderAddress
                ? a.senderAddress
                    .toString()
                    .localeCompare(b.senderAddress.toString())
                : -1,
          },
          render: (_, record) => (
            <>
              {record.senderAddress?.senderName} {record.senderAddress?.city}
            </>
          ),
        },
      ],
      [props.companies]
    )

    return (
      <Table
        pagination={statisticPagination}
        dataSource={tableData}
        columns={columns}
        loading={loading}
        rowKey="id"
        size="small"
      />
    )
  }
